<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import { getExerciseLibrary, saveExerciseToLibrary, getMuscleGroupName, getExerciseDisplayName } from '../services/db.js'
  import { getExerciseDisplayName as getEquipmentDisplayName } from '../data/equipment-types.js'

  export let selectedName = ''
  export let selectedId = null

  const dispatch = createEventDispatcher()

  let exercises = []
  let filteredExercises = []
  let isOpen = false
  let searchQuery = ''
  let showQuickAdd = false
  let quickAddName = ''
  let quickAddGroup = ''

  onMount(async () => {
    await loadExercises()
  })

  async function loadExercises() {
    exercises = await getExerciseLibrary()
    filteredExercises = [...exercises]
  }

  function toggleDropdown() {
    isOpen = !isOpen
    if (isOpen) {
      setTimeout(() => {
        document.querySelector('.selector-search')?.focus()
      }, 0)
    }
  }

  function closeDropdown() {
    isOpen = false
  }

  function filterExercises() {
    if (!searchQuery) {
      filteredExercises = [...exercises]
    } else {
      const query = searchQuery.toLowerCase()
      filteredExercises = exercises.filter(e =>
        e.name.toLowerCase().includes(query) ||
        (e.zhName && e.zhName.includes(searchQuery)) ||
        (e.description && e.description.toLowerCase().includes(query))
      )
    }
  }

  function selectExercise(exercise) {
    selectedId = exercise.id
    // Use Chinese name if available, then add equipment type
    const displayName = getExerciseDisplayName(exercise)
    selectedName = exercise.equipment_type
      ? getEquipmentDisplayName(displayName, exercise.equipment_type)
      : displayName
    closeDropdown()
    dispatch('select', {
      id: exercise.id,
      name: selectedName,
      equipment_type: exercise.equipment_type
    })
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      closeDropdown()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredExercises.length > 0) {
        selectExercise(filteredExercises[0])
      }
    }
  }

  function showQuickAddForm() {
    closeDropdown()
    showQuickAdd = true
    quickAddName = searchQuery
    setTimeout(() => {
      document.querySelector('.quick-add-name')?.focus()
    }, 0)
  }

  function hideQuickAddForm() {
    showQuickAdd = false
    quickAddName = ''
    quickAddGroup = ''
  }

  async function saveQuickAdd() {
    if (!quickAddName.trim()) {
      alert('請輸入運動名稱')
      return
    }
    if (!quickAddGroup) {
      alert('請選擇肌群')
      return
    }

    const exercise = await saveExerciseToLibrary({
      name: quickAddName.trim(),
      muscleGroup: quickAddGroup,
      description: ''
    })

    await loadExercises()
    selectExercise(exercise)
    hideQuickAddForm()
  }

  // Group exercises by muscle group
  $: groupedExercises = (() => {
    const grouped = {}
    filteredExercises.forEach(exercise => {
      const group = exercise.muscleGroup || 'other'
      if (!grouped[group]) {
        grouped[group] = []
      }
      grouped[group].push(exercise)
    })
    return grouped
  })()

  const groupOrder = ['chest', 'back', 'shoulders', 'arms', 'legs', 'core', 'cardio', 'other']

  function handleClickOutside(e) {
    if (isOpen && !e.target.closest('.exercise-selector')) {
      closeDropdown()
    }
  }
</script>

<svelte:window on:click={handleClickOutside} />

<div class="exercise-selector relative">
  <div class="exercise-selector-input">
    <button
      type="button"
      on:click={toggleDropdown}
      class="selector-trigger w-full px-3 py-2 text-left border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 flex justify-between items-center"
    >
      <span class="selector-value" class:text-gray-500={!selectedName} class:text-gray-900={selectedName}>
        {selectedName || '選擇運動項目'}
      </span>
      <span class="selector-arrow text-gray-400">▼</span>
    </button>
  </div>

  {#if isOpen}
    <div class="selector-dropdown absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
      <div class="p-2 border-b border-gray-200">
        <input
          type="text"
          class="selector-search w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="搜尋運動項目..."
          bind:value={searchQuery}
          on:input={filterExercises}
          on:keydown={handleKeydown}
        />
      </div>
      <div class="selector-options overflow-y-auto max-h-48">
        {#if filteredExercises.length === 0}
          <div class="px-3 py-2 text-sm text-gray-500">找不到運動項目</div>
        {:else}
          {#each groupOrder as group}
            {#if groupedExercises[group] && groupedExercises[group].length > 0}
              <div class="selector-group">
                <div class="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-100">
                  {getMuscleGroupName(group)}
                </div>
                {#each groupedExercises[group] as exercise}
                  <button
                    type="button"
                    class="selector-option w-full text-left px-3 py-2 text-sm cursor-pointer hover:bg-primary-50"
                    class:bg-primary-100={exercise.id === selectedId}
                    class:text-primary-700={exercise.id === selectedId}
                    on:click={() => selectExercise(exercise)}
                  >
                    {exercise.equipment_type
                      ? getEquipmentDisplayName(getExerciseDisplayName(exercise), exercise.equipment_type)
                      : getExerciseDisplayName(exercise)}
                    {#if exercise.isCustom}
                      <span class="text-xs text-gray-400 ml-1">(自訂)</span>
                    {/if}
                  </button>
                {/each}
              </div>
            {/if}
          {/each}
        {/if}
      </div>
      <div class="p-2 border-t border-gray-200">
        <button
          type="button"
          class="selector-add-new w-full px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-md text-left"
          on:click={showQuickAddForm}
        >
          + 新增運動項目
        </button>
      </div>
    </div>
  {/if}

  {#if showQuickAdd}
    <div class="quick-add-form mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
      <div class="mb-2">
        <input
          type="text"
          class="quick-add-name w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          placeholder="運動名稱"
          bind:value={quickAddName}
        />
      </div>
      <div class="mb-2">
        <select
          class="quick-add-group w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          bind:value={quickAddGroup}
        >
          <option value="">選擇肌群</option>
          <option value="chest">胸</option>
          <option value="back">背</option>
          <option value="shoulders">肩</option>
          <option value="arms">手臂</option>
          <option value="legs">腿</option>
          <option value="core">核心</option>
          <option value="cardio">有氧</option>
          <option value="other">其他</option>
        </select>
      </div>
      <div class="flex gap-2">
        <button
          type="button"
          class="px-3 py-1 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          on:click={saveQuickAdd}
        >
          儲存
        </button>
        <button
          type="button"
          class="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
          on:click={hideQuickAddForm}
        >
          取消
        </button>
      </div>
    </div>
  {/if}
</div>
